import React, { FC } from "react";

export interface SkeletonDynamicProps {
  type?:
    | "logo"
    | "largeSlider"
    | "newestAuthorSection"
    | "TopTrendingTopics"
    | "LatestArticles"
    | "topAuthorSection"
    | "becomeAnAuthor"
    | "header"
    | "centerAlignedheader"
    | "listItem"
    | "categoryGrid"
    | "founderSection"
    | "whatWeOffer"
    | "sectionHero"
    | "subscribeSection"
    | "blogPreviewHeader"
    | "sidebar"
    | "tags"
    | "trending"
    | "authors"
    | "popularPosts"
    | "singleContent"
    | "default"
  className?: string;
  showSubHeader?: boolean;
  showHeader?: boolean;
  centeredHeader?: boolean;
}

const HeaderSkeleton = ({
  centered = false,
  showSubHeader = true,
  showHeader = true,
  className = "",
}: {
  centered?: boolean;
  showSubHeader?: boolean;
  showHeader?: boolean;
  className?: string;
}) => (
  <div className={centered ? `text-center mb-10 ${className}` : `mb-6 ${className}`}>
    {showHeader && (
      <div className={`h-8 w-48 bg-neutral-200 dark:bg-neutral-700 rounded-md ${ centered ? "mx-auto mb-3" : "mb-2" }`} />
    )}

    {showSubHeader && (
      <div className={`h-4 w-80 bg-neutral-200 dark:bg-neutral-700 rounded-md ${ centered ? "mx-auto" : ""}`}
      />
    )}
  </div>
);

const SectionHeroSkeleton: FC<{ className?: string }> = ({ className = "" }) => {
  return (
    <div
      className={`nc-SectionHero relative ${className}`}>
      <div className="flex flex-col lg:flex-row space-y-10 lg:space-y-0 lg:space-x-10 items-center text-center lg:text-left animate-pulse">
        <div className="w-screen max-w-full xl:max-w-lg space-y-5 lg:space-y-7 px-4">
          <div className="h-12 w-4/5 bg-neutral-200 dark:bg-neutral-700 rounded-lg"/>
          <div className="h-4 w-full bg-neutral-200 dark:bg-neutral-700 rounded" />
          <div className="h-11 w-36 bg-neutral-200 dark:bg-neutral-700 rounded-full mt-4"/>
        </div>
        <div className="flex-grow w-full max-w-2xl px-4">
          <div className="h-80 w-full bg-neutral-200 dark:bg-neutral-700 rounded-3xl"/>
        </div>

      </div>
    </div>
  );
};

const SkeletonDynamic: FC<SkeletonDynamicProps> = ({
  type = "default",
  className = "",
  showSubHeader,
  showHeader,
  centeredHeader,
}) => {
  switch (type) {
    case "logo":
    return (
    <div className="flex items-center h-14 px-4">
      <div className="h-8 w-32 ml-40 bg-neutral-200 dark:bg-neutral-700 rounded-md animate-pulse" />
    </div>
  );

    case "header":
      return <HeaderSkeleton className={className} />;

    case "centerAlignedheader":
      return <HeaderSkeleton centered className={className} />;

    case "largeSlider":
      return (
        <div className={`pt-10 pb-16 md:py-16 lg:pb-28 lg:pt-20 ${className}`}>
          <HeaderSkeleton className="!mb-12 md:!mb-16" />

          <div className="nc-CardLarge1 relative flex flex-col-reverse md:flex-row justify-end animate-pulse">
            <div className="md:absolute z-10 md:left-0 md:top-1/2 md:-translate-y-1/2 w-full -mt-8 md:mt-0 px-3 sm:px-6 md:px-0 md:w-3/5 lg:w-1/2 xl:w-2/5">
              <div className="p-4 sm:p-8 xl:py-14 md:px-10 bg-white/40 dark:bg-neutral-800/40 backdrop-blur-lg shadow-lg rounded-3xl space-y-3 sm:space-y-5">
                <div className="flex flex-wrap gap-2">
                  <div className="w-[70px] h-6 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                </div>

                <div className="h-8 w-4/5 bg-neutral-200 dark:bg-neutral-700 rounded-md" />

                <div className="flex items-center gap-3 mt-4">
                  <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-4 w-3/5 bg-neutral-200 dark:bg-neutral-700 rounded" />
                    <div className="h-3 w-2/5 bg-neutral-200 dark:bg-neutral-700 rounded" />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                    <div className="w-8 h-8 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                  </div>
                  <div className="w-9 h-9 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                </div>
              </div>

              <div className="p-4 sm:pt-8 sm:px-10 flex space-x-2">
                <div className="w-11 h-11 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                <div className="w-11 h-11 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
              </div>
            </div>

            <div className="w-full md:w-4/5 lg:w-2/3">
              <div className="aspect-w-16 aspect-h-12 sm:aspect-h-9 md:aspect-h-14 lg:aspect-h-10 2xl:aspect-h-9 relative">
                <div className="absolute inset-0 bg-neutral-200 dark:bg-neutral-700 rounded-3xl" />              
              </div>
            </div>
          </div>
        </div>
      );
    
    case "newestAuthorSection":
      return (
        <div className={`animate-pulse ${className}`}>
          <HeaderSkeleton centered className="!mb-12 md:!mb-16" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 pb-12 md:pb-16">
            {Array.from({ length: 5 }).map((_, i) => (
              <div 
              key={i} 
              className="nc-CardAuthorBox2 flex flex-col overflow-hidden rounded-3xl shadow-sm"
              >
                <div className="relative flex-shrink-0">
                  <div className="bg-gray-300 dark:bg-neutral-700 w-full aspect-w-7 aspect-h-5 sm:aspect-h-6"></div>
                  <div className="absolute top-3 inset-x-3 flex">
                    <div className="py-1 px-4 bg-gray-200 dark:bg-neutral-700 rounded-full text-xs w-20 h-5"></div>
                  </div>
                </div>
                <div className="-mt-8 m-8 text-center">
                  <div className="mx-auto bg-gray-300 dark:bg-neutral-700 rounded-full w-16 h-16 ring-2 ring-white"></div>
                  <div className="mt-3">
                    <div className="bg-gray-300 dark:bg-neutral-700 h-4 w-24 mx-auto rounded mb-2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-4">
            <div className="w-12 h-12 rounded-full bg-neutral-200 dark:bg-neutral-700 mr-2" />
            <div className="w-12 h-12 rounded-full bg-neutral-200 dark:bg-neutral-700" />
          </div>
        </div>
      );

    case "TopTrendingTopics":
  return (
    <div className={`animate-pulse relative ${className}`}>
      <div className="flex items-center justify-between mb-8">
        <HeaderSkeleton />
        <div className="flex gap-3">
          <div className="h-10 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700" />
          <div className="h-10 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        </div>
      </div>

      <div className="flex gap-8 overflow-hidden py-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <div 
          key={i} 
          className="flex flex-col w-[200px] sm:w-[220px] flex-shrink-0"
          >
            <div className="relative w-full h-[140px] sm:h-[150px] rounded-2xl overflow-hidden bg-neutral-200 dark:bg-neutral-700">
              <div className="absolute top-3 left-3 h-6 w-8 rounded-full bg-neutral-300 dark:bg-neutral-600" />
            </div>

            <div className="mt-4 flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-neutral-200 dark:bg-neutral-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                <div className="h-3 w-1/2 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  
  case "LatestArticles":
      return (
        <div className={`animate-pulse relative ${className}`}>
         <HeaderSkeleton />
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-3 mt-6 mb-8">
              {["14", "24", "20", "16"].map((w, i) => (
                <div
                  key={i}
                  className={`h-7 w-${w} bg-neutral-200 dark:bg-neutral-700 rounded-full`}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="h-7 w-14 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-pulse w-4/5">
            <div className="col-span-2 rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700 flex flex-col animate-pulse">
              <div className="h-64 bg-neutral-200 dark:bg-neutral-700 w-full" />
              <div className="p-5 space-y-4">
                <div className="h-4 w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                <div className="h-6 w-4/5 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                <div className="h-6 w-3/5 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                <div className="h-4 w-full bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                <div className="h-4 w-5/6 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                <div className="flex items-center gap-3 mt-4">
                  <div className="h-10 w-10 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                  <div className="flex flex-col gap-2">
                    <div className="h-4 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                    <div className="h-3 w-16 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                  </div>
                </div>
                <div className="flex justify-between items-center mt-3">
                  <div className="flex gap-4">
                    <div className="h-4 w-10 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                    <div className="h-4 w-10 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                  </div>
                  <div className="h-4 w-20 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-1 w-[115%] flex flex-col gap-6 m-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700 flex items-center justify-between p-5 gap-6"
                >
                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                    <div className="h-5 w-4/5 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                    <div className="flex items-center gap-3 pt-2">
                      <div className="h-6 w-6 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                      <div className="h-4 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      <div className="flex gap-4">
                        <div className="h-4 w-10 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                        <div className="h-4 w-10 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                      </div>
                      <div className="h-4 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                    </div>
                  </div>

                  <div className="h-24 w-32 bg-neutral-200 dark:bg-neutral-700 rounded-xl flex-shrink-0 ml-3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      );

    case "topAuthorSection":
      return (
        <div className={`animate-pulse relative ${className}`}>
         <HeaderSkeleton centered />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-center border border-neutral-200 dark:border-neutral-700 rounded-3xl p-6 space-y-4"
              >
                <div className="h-20 w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                <div className="h-4 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
                <div className="h-8 w-16 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      );

    case "becomeAnAuthor":
   return (
    <div className={`animate-pulse relative ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="space-y-4">
        <div className="h-4 w-40 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-8 w-80 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-20 w-72 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-10 w-40 bg-neutral-200 dark:bg-neutral-700 rounded-md mt-6" />
        </div>
      <div className="w-full h-72 lg:h-96 bg-neutral-200 dark:bg-neutral-700 rounded-3xl" />
    </div>
    </div>
  );
  
  case "listItem":
  return (
    <div className={`animate-pulse ${centeredHeader ? "text-center w-full" : ""}`}>
      {centeredHeader && (
        <div className="mb-10">
          <div className="h-7 w-48 bg-neutral-200 dark:bg-neutral-700 rounded-md mx-auto mb-3" />
          <div className="h-4 w-32 bg-neutral-200 dark:bg-neutral-700 rounded-md mx-auto" />
        </div>
      )}

      <div className={`border border-neutral-200 dark:border-neutral-700 rounded-3xl overflow-hidden shadow-sm flex flex-col ${className}`}>

        {showSubHeader && (
          <div className="relative w-full h-[200px] bg-neutral-200 dark:bg-neutral-700">
            <div className="absolute top-3 left-3 h-6 w-20 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
          </div>
        )}

        <div className="p-5 flex flex-col space-y-4">

          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
            <div className="flex-1">
              <div className="h-3 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-1" />
              <div className="h-3 w-1/2 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
            </div>
          </div>

          <div className="h-5 w-5/6 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
          <div className="h-5 w-2/3 bg-neutral-200 dark:bg-neutral-700 rounded-md" />

          <div className="flex justify-between items-center pt-2">
            <div className="flex gap-4">
              <div className="h-6 w-6 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
              <div className="h-6 w-6 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
            </div>
            <div className="h-6 w-6 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
  
  case "categoryGrid":
  return (
    <div className={`animate-pulse relative ${className}`}>
      <div className="text-center mb-10">
        <div className="h-8 w-64 mx-auto bg-neutral-200 dark:bg-neutral-700 rounded-md mb-3" />
        <div className="h-4 w-48 mx-auto bg-neutral-200 dark:bg-neutral-700 rounded-md" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 sm:gap-6 md:gap-8">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col items-center justify-center border border-neutral-200 dark:border-neutral-700 rounded-3xl p-6 space-y-4"
          >
            <div className="w-full h-24 sm:h-28 md:h-32 bg-neutral-200 dark:bg-neutral-700 rounded-2xl" />
            <div className="h-4 w-2/3 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
            <div className="h-3 w-1/3 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );

case "founderSection":
  return (
    <div className={`animate-pulse relative ${className}`}>
      <div className="text-left mb-10">
        <div className="h-8 w-40 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-3" />
        <div className="h-4 w-[500px] max-w-full bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-4 w-[420px] max-w-full mt-2 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="max-w-sm">
            <div className="relative w-full h-[300px] rounded-xl overflow-hidden mb-4 bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-5 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-2" />
            <div className="h-4 w-1/2 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );

case "whatWeOffer":
  return (
    <div className="px-5 py-16 animate-pulse">
      <div className="mb-10">
        <div className="h-7 w-48 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-4" />
        <div className="h-4 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 xl:gap-8">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="p-6 bg-neutral-100 dark:bg-neutral-800/30 rounded-2xl border border-neutral-200 dark:border-neutral-700">
            <div className="h-5 w-2/3 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-3" />
            <div className="h-4 w-full bg-neutral-200 dark:bg-neutral-700 rounded-md mb-2" />
            <div className="h-4 w-5/6 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );

case "sectionHero":
  return <SectionHeroSkeleton className={className} />;

case "subscribeSection":
  return (
    <div className="flex flex-col lg:flex-row items-center animate-pulse">
      <div className="w-full lg:w-2/5 space-y-4">
        <div className="h-9 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-5 w-11/12 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-12 w-full bg-neutral-200 dark:bg-neutral-700 rounded-xl mt-4" />
      </div>

      <div className="flex-grow mt-10 lg:mt-0 lg:ml-10 w-full max-w-md">
        <div className="h-64 w-full bg-neutral-200 dark:bg-neutral-700 rounded-3xl" />
      </div>
    </div>
  );

case "blogPreviewHeader":
  return (
    <div className="relative pt-16 z-10 md:py-20 lg:py-28 bg-neutral-900 dark:bg-black animate-pulse">
      <div className="container relative z-10">
        <div className="max-w-screen-md space-y-4 px-4">
          <div className="h-5 w-32 bg-neutral-700 rounded-md"></div>
          <div className="h-8 w-3/4 bg-neutral-700 rounded-md"></div>
          <div className="h-4 w-32 bg-neutral-700 rounded-md"></div>
          <div className="flex justify-end">
            <div className="flex items-center gap-2">
              <div className="h-7 w-10 bg-neutral-700 rounded-full"></div>
              <div className="h-7 w-10 bg-neutral-700 rounded-full"></div>
              <div className="h-7 w-10 bg-neutral-700 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 md:mt-0 md:absolute md:top-0 md:right-0 md:bottom-0 md:w-1/2 lg:w-2/5 2xl:w-1/3">
        <div className="hidden md:block absolute top-0 left-0 bottom-0 w-1/5 from-neutral-900 dark:from-black bg-gradient-to-r"></div>
        <div className="block w-full h-full bg-neutral-800 dark:bg-neutral-700"></div>
      </div>
    </div>
  );

case "tags":
  return (
    <div className="bg-neutral-100 dark:bg-neutral-900 rounded-3xl overflow-hidden p-4 xl:p-5 w-full min-h-[350px] animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-5 w-40 bg-neutral-700 rounded-md"></div>
        <div className="h-4 w-16 bg-neutral-700 rounded-md"></div>
      </div>

      <div className="w-full h-[1px] bg-neutral-300 mb-4"></div>

      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 15 }).map((_, i) => (
          <div key={i} className="h-8 w-28 bg-neutral-300 rounded-lg"></div>
        ))}
      </div>
    </div>
  );

case "trending":
  return (
    <div className="bg-neutral-100 dark:bg-neutral-900 rounded-3xl overflow-hidden p-4 xl:p-5 w-full animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-5 w-36 bg-neutral-700 rounded-md"></div>
        <div className="h-4 w-14 bg-neutral-700 rounded-md"></div>
      </div>

      <div className="w-full h-[1px] bg-neutral-300 dark:bg-neutral-700 mb-4"></div>

      <div className="space-y-4 w-full">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 p-4 bg-neutral-100 dark:bg-neutral-900 rounded-2xl"
          >
            <div className="h-16 w-16 bg-neutral-300 dark:bg-neutral-700 rounded-md flex-shrink-0"></div>
            <div className="flex-1 flex flex-col justify-center gap-2">
              <div className="h-4 w-32 bg-neutral-300 dark:bg-neutral-700 rounded-md"></div>
              <div className="h-4 w-24 bg-neutral-300 dark:bg-neutral-700 rounded-md"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

case "authors":
  return (
    <div className="bg-neutral-100 dark:bg-neutral-900 rounded-3xl overflow-hidden p-4 xl:p-5 w-full animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-5 w-36 bg-neutral-700 rounded-md"></div>
        <div className="h-4 w-14 bg-neutral-700 rounded-md"></div>
      </div>

      <div className="flex flex-col divide-y divide-neutral-200 dark:divide-neutral-700">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-4">
            <div className="h-12 w-12 bg-neutral-300 dark:bg-neutral-700 rounded-full"></div>

            <div className="flex-1">
              <div className="h-4 w-32 bg-neutral-300 dark:bg-neutral-700 rounded-md mb-2"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

case "popularPosts":
  return (
    <div className="bg-neutral-100 dark:bg-neutral-900 rounded-3xl overflow-hidden p-4 xl:p-5 w-full min-h-[350px] animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-5 w-40 bg-neutral-700 rounded-md"></div>
        <div className="h-4 w-16 bg-neutral-700 rounded-md"></div>
      </div>

      <div className="w-full h-[1px] bg-neutral-300 mb-4"></div>

      <div className="space-y-4 w-full">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-4 p-4 bg-neutral-100 dark:bg-neutral-900 rounded-2xl">
            <div className="flex flex-col flex-1 gap-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-neutral-300 dark:bg-neutral-700"></div>
                <div className="h-4 w-40 bg-neutral-300 dark:bg-neutral-700 rounded-md"></div>
              </div>

              <div className="h-4 w-full bg-neutral-300 dark:bg-neutral-700 rounded-md mt-2"></div>
              <div className="h-4 w-5/6 bg-neutral-300 dark:bg-neutral-700 rounded-md"></div>
            </div>

            <div className="h-16 w-24 bg-neutral-300 dark:bg-neutral-700 rounded-md flex-shrink-0"></div>
          </div>
        ))}
      </div>
    </div>
  );

  case "singleContent":
  return (
    <div className={`nc-SingleContent space-y-10 animate-pulse ${className}`}>
      <div className="editor prose lg:prose-lg !max-w-screen-md mx-auto dark:prose-invert space-y-3">
        <div className="h-5 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
        <div className="h-5 w-full bg-neutral-200 dark:bg-neutral-700 rounded-md" />
      </div>
      <div className="max-w-screen-md mx-auto flex flex-wrap gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-6 w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full"/>
        ))}
      </div>
      <div className="max-w-screen-md mx-auto border-b border-t border-neutral-100 dark:border-neutral-700 py-6" />
      <div className="max-w-screen-md mx-auto flex items-center gap-4">
        <div className="h-12 w-12 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <div className="flex-1">
          <div className="h-4 w-32 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-2" />
          <div className="h-3 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
          <div className="h-3 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md mt-2" />
        </div>
      </div>

      <div className="max-w-screen-md mx-auto pt-5">
        <div className="h-6 w-40 bg-neutral-200 dark:bg-neutral-700 rounded-md mb-5" />

        <div className="h-28 w-full bg-neutral-200 dark:bg-neutral-700 rounded-xl mb-3" />

        <div className="flex justify-start gap-2">
          <div className="h-10 w-28 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
          <div className="h-10 w-28 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
        </div>

        <div className="mt-8 space-y-6 animate-pulse">
          {[...Array(3)].map((_, i) => (
        <div key={i} className="flex gap-3">
          
          <div className="h-10 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700" />

          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
            <div className="h-4 w-5/6 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
            <div className="h-4 w-4/6 bg-neutral-200 dark:bg-neutral-700 rounded-md" />
          </div>
        </div>
      ))}
      </div>
      
      <div className="flex justify-center mt-6">
        <div className="h-10 w-32 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
        </div>
      </div>
    </div>
  );

    default:
      return (
        <div className={`animate-pulse space-y-3 ${className}`}>
          <div className="h-48 w-full bg-neutral-200 dark:bg-neutral-700 rounded-2xl" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"></div>
        </div>
      );
  }
};

export default SkeletonDynamic;

